import type {
  HeatPoint, DangerZone,
  RuleKey, TimeSlot, DayOfWeek,
} from './heatmap-types';
import { RULE_KEY_ORDER, RULE_VISUALS } from './heatmap-types';
import {
  loadHeatmapPageData, resetHeatmapPageData,
  fetchHeatPoints,
  type HeatmapPageData,
} from './heatmap-data';
import { clusterPoints, filterPoints, reverseGeocode } from './heatmap-clustering';
import {
  initMap, setupLayers, setHeatPoints, setLayerVisible,
  setHeatRadius, addDangerZoneMarkers,
  flyToBounds, set3DMode, is3DActive, resetMapState,
  setMapTheme,
} from './heatmap-map';
import { exportHeatmapXlsx } from './heatmap-export';
import { state } from '../state';
import { escapeHtml, escapeHtmlAttr } from '../utils/html';

// ── Module state ───────────────────────────────────────────────────────────

let _wired       = false;
let _map:          ReturnType<typeof initMap> | null = null;
let _mapReady    = false;
let _mapObserver: ResizeObserver | null = null;
let _pageData:  HeatmapPageData | null = null;

let _allPoints:      HeatPoint[]  = [];
let _filteredPoints: HeatPoint[]  = [];
let _dangerZones:    DangerZone[] = [];

let _activeTimeSlot: TimeSlot      = 'all';
let _activeDays:     Set<DayOfWeek> = new Set([0, 1, 2, 3, 4, 5, 6]);
let _activeRuleKeys: Set<RuleKey>  = new Set(RULE_KEY_ORDER);
let _selectedRuleIds: Map<RuleKey, Set<string>> = new Map();
let _selectedHmDeviceIds: Set<string> | null = null;
let _hmVsWired = false;

// ── Public API ─────────────────────────────────────────────────────────────

export function initHeatmapPage(): void {
  if (_wired) return;
  _wired = true;

  initDateRange();
  wireQuickRanges();
  wireRunButton();
  wireExportButton();
  wireEventsModal();
  wireHowItWorksModal();
  wireRadiusSlider();
  wireToggle3D();
  wireDetailPanelClose();
  wireThemeChange();

  showState('empty');
  loadPageData();
}

export function resetHeatmapPage(): void {
  _mapObserver?.disconnect(); _mapObserver = null;
  resetMapState();
  if (_map) { _map.remove(); _map = null; }
  _mapReady    = false;
  _wired       = false;
  _allPoints   = []; _filteredPoints = []; _dangerZones = [];
  _activeTimeSlot      = 'all';
  _activeDays          = new Set([0, 1, 2, 3, 4, 5, 6]);
  _activeRuleKeys      = new Set(RULE_KEY_ORDER);
  _selectedRuleIds     = new Map();
  _selectedHmDeviceIds = null;
  _hmVsWired           = false;
  el('hmEventsModal')?.classList.remove('show');
  el('hmHowModal')?.classList.remove('show');
  closeDropdowns();
  resetHeatmapPageData();
  showState('empty');
}

// ── Page data load ─────────────────────────────────────────────────────────

async function loadPageData(): Promise<void> {
  try {
    _pageData = await loadHeatmapPageData();
    _selectedRuleIds.clear();
    for (const [key, ids] of _pageData.ruleIdsMap) {
      _selectedRuleIds.set(key, new Set([ids[0]])); // radio: one rule per category
    }
    _activeRuleKeys = new Set([..._pageData.ruleIdsMap.keys()]);
    buildRuleChips();
    buildDayToggles();
    buildHmVehicleSelector();
    const runBtn = el<HTMLButtonElement>('heatmapRunBtn');
    if (runBtn) runBtn.disabled = false;
  } catch (e) {
    showError('Failed to load page data: ' + (e as Error).message);
  }
}

// ── Effective rule IDs ─────────────────────────────────────────────────────

function buildEffectiveRuleIdsMap(): Map<RuleKey, string[]> {
  const result = new Map<RuleKey, string[]>();
  for (const key of _activeRuleKeys) {
    const selected = _selectedRuleIds.get(key);
    if (selected && selected.size > 0) result.set(key, [...selected]);
  }
  return result;
}

// ── Date range ─────────────────────────────────────────────────────────────

function initDateRange(): void {
  const to = new Date();
  const from = new Date(); from.setDate(from.getDate() - 30);
  setDateInput('heatmapFrom', from);
  setDateInput('heatmapTo',   to);
}

function setDateInput(id: string, d: Date): void {
  const inp = el<HTMLInputElement>(id);
  if (inp) inp.value = d.toISOString().slice(0, 10);
}

function wireQuickRanges(): void {
  document.querySelectorAll('[data-hm-range]').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('[data-hm-range]').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const range = (btn as HTMLElement).dataset.hmRange!;
      const to = new Date(), from = new Date();
      if (range === '7d')  from.setDate(from.getDate() - 7);
      if (range === '30d') from.setDate(from.getDate() - 30);
      if (range === 'mtd') from.setDate(1);
      setDateInput('heatmapFrom', from);
      setDateInput('heatmapTo',   to);
    });
  });
}

// ── Rule chips with dropdown for multi-rule categories ─────────────────────

function closeDropdowns(): void {
  document.querySelectorAll('.hm-rule-dropdown.open').forEach(d => d.classList.remove('open'));
}

function buildRuleChips(): void {
  const container = el('heatmapRuleToggles');
  if (!container || !_pageData) return;
  container.innerHTML = '';
  document.removeEventListener('click', closeDropdowns);
  document.addEventListener('click', closeDropdowns);

  for (const key of RULE_KEY_ORDER) {
    const v        = RULE_VISUALS[key];
    const matched  = _pageData.matchedRuleNames.get(key) ?? [];
    const hasRules = matched.length > 0;
    const isActive = _activeRuleKeys.has(key) && hasRules;

    const group = document.createElement('div');
    group.className = 'hm-rule-chip-group';

    // ── Pill chip ─────────────────────────────────────────────────────────
    const chip = document.createElement('button');
    chip.className = `hm-rule-chip${isActive ? ' active' : ''}${!hasRules ? ' hm-rule-disabled' : ''}`;
    chip.dataset.hmRule = key;
    chip.style.color = v.color;
    chip.style.setProperty('--chip-color', v.color);

    chip.innerHTML = `
      <span class="hm-rule-chip-icon">${v.icon}</span>
      <span class="hm-rule-chip-body">
        <span class="hm-rule-chip-label">${v.label}</span>
        <span class="hm-rule-chip-sub"></span>
      </span>
      <span class="hm-rule-chip-arrow"><svg viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,1 5,5 9,1"/></svg></span>
    `;

    // Set sub-label safely (selected rule name)
    const selIds = _selectedRuleIds.get(key);
    const selRule = selIds ? matched.find(r => selIds.has(r.id)) : null;
    const subEl = chip.querySelector<HTMLElement>('.hm-rule-chip-sub')!;
    subEl.textContent = selRule?.name ?? (matched.length === 1 ? matched[0].name : '');

    // Main chip body click = toggle (arrow click is handled separately)
    if (hasRules) {
      chip.addEventListener('click', (e) => {
        if ((e.target as Element).closest('.hm-rule-chip-arrow')) return;
        toggleRuleKey(key, chip);
      });
    }
    group.appendChild(chip);

    // ── Dropdown (always created, shows all matched rules as radio buttons) ──
    const dropdown = document.createElement('div');
    dropdown.className = 'hm-rule-dropdown';

    const hdr = document.createElement('div');
    hdr.className = 'hm-dropdown-header';
    hdr.textContent = v.label + ' — select rule';
    dropdown.appendChild(hdr);

    if (!hasRules) {
      const noMatch = document.createElement('div');
      noMatch.className   = 'hm-dropdown-no-match';
      noMatch.textContent = 'No matching rules in this account';
      dropdown.appendChild(noMatch);
    } else {
      for (const rule of matched) {
        const row = document.createElement('label');
        row.className = 'hm-dropdown-row';

        const rb = document.createElement('input');
        rb.type    = 'radio';
        rb.name    = `hm-rule-${key}`;
        rb.value   = rule.id;
        rb.checked = selIds?.has(rule.id) ?? false;
        rb.style.accentColor = v.color;
        rb.addEventListener('change', e => {
          e.stopPropagation();
          if (!rb.checked) return;
          const sel = _selectedRuleIds.get(key);
          if (!sel) return;
          sel.clear();
          sel.add(rule.id);
          subEl.textContent = rule.name;
          if (!_activeRuleKeys.has(key)) toggleRuleKey(key, chip);
          else if (_map && _mapReady) applyFilters();
        });

        const span = document.createElement('span');
        span.className   = 'hm-dropdown-rule-name';
        span.textContent = rule.name;
        row.appendChild(rb);
        row.appendChild(span);
        dropdown.appendChild(row);
      }
    }

    // Arrow click opens/closes dropdown
    chip.querySelector<HTMLElement>('.hm-rule-chip-arrow')!.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = dropdown.classList.contains('open');
      closeDropdowns();
      if (!isOpen) {
        const rect = chip.getBoundingClientRect();
        dropdown.style.top  = `${rect.bottom + 6}px`;
        dropdown.style.left = `${rect.left}px`;
        dropdown.classList.add('open');
      }
    });
    dropdown.addEventListener('click', e => e.stopPropagation());

    document.body.appendChild(dropdown);
    container.appendChild(group);
  }
}

function toggleRuleKey(key: RuleKey, chip: HTMLElement): void {
  const color = RULE_VISUALS[key].color;
  if (_activeRuleKeys.has(key)) {
    _activeRuleKeys.delete(key);
    chip.classList.remove('active');
  } else {
    _activeRuleKeys.add(key);
    chip.classList.add('active');
  }
  if (_map && _mapReady) {
    setLayerVisible(_map, key, _activeRuleKeys.has(key));
    applyFilters();
  }
}

// ── Day toggles ─────────────────────────────────────────────────────────────

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function buildDayToggles(): void {
  const container = el('heatmapDayToggles');
  if (!container) return;
  container.innerHTML = '';
  for (let i = 0; i < 7; i++) {
    const btn = document.createElement('button');
    btn.className = 'qr-btn active';
    btn.dataset.hmDay = String(i);
    btn.textContent = DAY_LABELS[i];
    btn.addEventListener('click', () => {
      const day = i as DayOfWeek;
      if (_activeDays.has(day)) {
        if (_activeDays.size === 1) return;
        _activeDays.delete(day);
        btn.classList.remove('active');
      } else {
        _activeDays.add(day);
        btn.classList.add('active');
      }
      applyFilters();
    });
    container.appendChild(btn);
  }
}

// ── Run analysis ───────────────────────────────────────────────────────────

function wireRunButton(): void {
  el<HTMLButtonElement>('heatmapRunBtn')?.addEventListener('click', runAnalysis);
}

async function runAnalysis(): Promise<void> {
  if (!_pageData) return;

  const fromInput = el<HTMLInputElement>('heatmapFrom');
  const toInput   = el<HTMLInputElement>('heatmapTo');
  if (!fromInput?.value || !toInput?.value) return;

  const fromISO    = fromInput.value + 'T00:00:00';
  const toISO      = toInput.value   + 'T23:59:59';
  const deviceIds  = _selectedHmDeviceIds ?? new Set(_pageData.deviceList.map(d => d.id));
  const ruleIdsMap = buildEffectiveRuleIdsMap();

  if (ruleIdsMap.size === 0) {
    showError('No rules selected. Enable at least one rule category that has matched rules in your account.');
    return;
  }

  showState('loading');
  setLoadingLabel('Preparing analysis…');

  try {
    const points = await fetchHeatPoints({ fromISO, toISO, deviceIds, ruleIdsMap }, setLoadingLabel);

    _allPoints = points;

    if (points.length === 0) {
      showError('No events with GPS data found for this date range. Try extending the range or check that your Geotab account has LogRecord data.');
      return;
    }

    setLoadingLabel('Clustering danger zones…');
    _filteredPoints = filterPoints(points, _activeRuleKeys, _activeTimeSlot, _activeDays);
    _dangerZones    = clusterPoints(_filteredPoints);

    showState('results');

    if (!_map) {
      _map = initMap('heatmapMapContainer');
      _map.on('load', () => {
        _mapReady = true;
        setupLayers(_map!);
        renderMapData();
      });
      const mapArea = el('heatmapMapContainer')?.parentElement;
      if (mapArea && !_mapObserver) {
        _mapObserver = new ResizeObserver(() => { if (_map && _mapReady) _map.resize(); });
        _mapObserver.observe(mapArea);
      }
    } else {
      renderMapData();
    }

    renderZoneTable(_dangerZones);
    renderHeatmapKpis(_filteredPoints, _dangerZones);
    renderTopOffenders(_filteredPoints);
    renderEventsTable(_filteredPoints);
    showExportButton(true);
    geocodeZonesBackground(_dangerZones);

  } catch (e) {
    showError('Analysis failed: ' + (e as Error).message);
  }
}

function renderMapData(): void {
  if (!_map || !_mapReady) return;
  _map.resize(); // sync canvas to current container dimensions
  setHeatPoints(_map, _filteredPoints);
  for (const key of RULE_KEY_ORDER) {
    setLayerVisible(_map, key, _activeRuleKeys.has(key));
  }
  addDangerZoneMarkers(_map, _dangerZones, onZoneClick);
  flyToBounds(_map, _filteredPoints);
}

// ── Filter re-apply ────────────────────────────────────────────────────────

function applyFilters(): void {
  if (!_map || !_mapReady || _allPoints.length === 0) return;
  _filteredPoints = filterPoints(_allPoints, _activeRuleKeys, _activeTimeSlot, _activeDays);
  setHeatPoints(_map, _filteredPoints);
  _dangerZones = clusterPoints(_filteredPoints);
  addDangerZoneMarkers(_map, _dangerZones, onZoneClick);
  renderZoneTable(_dangerZones);
  renderHeatmapKpis(_filteredPoints, _dangerZones);
  renderTopOffenders(_filteredPoints);
  renderEventsTable(_filteredPoints);
  geocodeZonesBackground(_dangerZones);
}

// ── Radius slider ──────────────────────────────────────────────────────────

function wireRadiusSlider(): void {
  const slider = el<HTMLInputElement>('heatmapRadiusSlider');
  const label  = el('heatmapRadiusVal');
  slider?.addEventListener('input', () => {
    const v = Number(slider.value);
    if (label) label.textContent = `${v}px`;
    if (_map && _mapReady) setHeatRadius(_map, v);
  });
}

// ── 3D toggle ──────────────────────────────────────────────────────────────

function wireToggle3D(): void {
  const btn = el('heatmapToggle3D');
  if (!btn) return;
  btn.addEventListener('click', () => {
    if (!_map) return;
    const next = !is3DActive();
    set3DMode(_map, next);
    btn.classList.toggle('active', next);
  });
}

// ── Detail panel ────────────────────────────────────────────────────────────

function wireDetailPanelClose(): void {
  el('heatmapDetailClose')?.addEventListener('click', () => {
    el('heatmapDetailPanel')?.classList.remove('active');
  });
}

function wireThemeChange(): void {
  document.addEventListener('fleet-theme-change', (e) => {
    const isLight = (e as CustomEvent<{ isLight: boolean }>).detail.isLight;
    if (!_map) return;
    setMapTheme(_map, isLight, (map) => {
      _mapReady = false;
      setupLayers(map);
      _mapReady = true;
      if (_filteredPoints.length) {
        setHeatPoints(map, _filteredPoints);
        for (const key of RULE_KEY_ORDER) setLayerVisible(map, key, _activeRuleKeys.has(key));
      }
      if (_dangerZones.length) {
        addDangerZoneMarkers(map, _dangerZones, onZoneClick);
      }
    });
  });
}

// ── Events sheet modal ──────────────────────────────────────────────────────

function wireEventsModal(): void {
  el('hmEventsPill')?.addEventListener('click', () => {
    el('hmEventsModal')?.classList.add('show');
  });
  el('hmEventsModalClose')?.addEventListener('click', () => {
    el('hmEventsModal')?.classList.remove('show');
  });
  el('hmEventsModal')?.addEventListener('click', e => {
    if (e.target === el('hmEventsModal')) el('hmEventsModal')!.classList.remove('show');
  });
}

function wireHowItWorksModal(): void {
  el('heatmapHowBtn')?.addEventListener('click', () => {
    el('hmHowModal')?.classList.add('show');
  });
  el('hmHowModalClose')?.addEventListener('click', () => {
    el('hmHowModal')?.classList.remove('show');
  });
  el('hmHowModal')?.addEventListener('click', e => {
    if ((e.target as Element).id === 'hmHowModal') el('hmHowModal')!.classList.remove('show');
  });
}

function onZoneClick(zone: DangerZone): void {
  el('heatmapDetailTitle')!.textContent = zone.locationName ?? `Zone #${zone.rank}`;
  const body  = el('heatmapDetailBody')!;
  const total = zone.totalViolations;
  const pct   = (n: number) => total > 0 ? Math.round((n / total) * 100) : 0;

  // Peak-hour chart
  const hourCounts = new Array(24).fill(0);
  for (const p of zone.points) hourCounts[new Date(p.timestamp).getHours()]++;
  const maxCount = Math.max(...hourCounts, 1);
  const zoneColor = RULE_VISUALS[zone.dominantRule].color;
  const hourChart = `
    <div class="hm-detail-section-title">Violations by Hour of Day</div>
    <div class="hm-hour-chart">
      ${hourCounts.map((count, h) => {
        const barPct = Math.round((count / maxCount) * 100);
        const height = count > 0 ? Math.max(barPct, 8) : 0;
        const opacity = barPct > 40 ? 'dd' : '66';
        return `<div class="hm-hour-bar" title="${String(h).padStart(2, '0')}:00 — ${count} events">
          <div class="hm-hour-fill" style="height:${height}%;background:${zoneColor}${opacity}"></div>
          <div class="hm-hour-label">${h % 6 === 0 ? h + 'h' : ''}</div>
        </div>`;
      }).join('')}
    </div>`;

  let ruleRows = '';
  for (const key of RULE_KEY_ORDER) {
    const count = zone.byRule[key] ?? 0;
    if (!count) continue;
    const p = pct(count);
    const color = RULE_VISUALS[key].color;
    ruleRows += `
      <div class="hm-detail-row">
        <span class="hm-rule-dot" style="background:${color};flex-shrink:0"></span>
        <span class="hm-detail-key">${RULE_VISUALS[key].label}</span>
        <span class="hm-detail-val">${count} <span class="hm-muted">(${p}%)</span></span>
        <div class="hm-detail-bar" style="background:${color}33">
          <div style="width:${p}%;height:100%;background:${color};border-radius:3px"></div>
        </div>
      </div>`;
  }

  const peakStr = zone.peakHour != null
    ? `${String(zone.peakHour).padStart(2, '0')}:00–${String(zone.peakHour + 1).padStart(2, '0')}:00`
    : '—';

  const deviceMap    = new Map(_pageData?.deviceList.map(d => [d.id, d.name]) ?? []);
  const vehicleNames = [...zone.uniqueDeviceIds].map(id => deviceMap.get(id) ?? id).sort();
  const driverNames  = [...zone.uniqueDriverIds]
    .map(id => _pageData?.userMap.get(id) ?? id)
    .filter(n => n && n !== 'UnknownDriverId')
    .sort();

  const vehicleSection = vehicleNames.length > 0 ? `
    <div class="hm-detail-section-title">Vehicles Involved (${vehicleNames.length})</div>
    <div class="hm-detail-entity-list">
      ${vehicleNames.map(n => `<span class="hm-detail-entity-tag">${escapeHtml(n)}</span>`).join('')}
    </div>` : '';

  const driverSection = driverNames.length > 0 ? `
    <div class="hm-detail-section-title">Drivers Flagged (${driverNames.length})</div>
    <div class="hm-detail-entity-list">
      ${driverNames.map(n => `<span class="hm-detail-entity-tag">${escapeHtml(n)}</span>`).join('')}
    </div>` : '';

  body.innerHTML = `
    <div class="hm-detail-stats">
      <div class="hm-detail-kpi"><div class="hm-detail-kpi-val">${total}</div><div class="hm-detail-kpi-lbl">Violations</div></div>
      <div class="hm-detail-kpi"><div class="hm-detail-kpi-val">${zone.uniqueDeviceIds.size}</div><div class="hm-detail-kpi-lbl">Vehicles</div></div>
      <div class="hm-detail-kpi"><div class="hm-detail-kpi-val">${zone.uniqueDriverIds.size}</div><div class="hm-detail-kpi-lbl">Drivers</div></div>
      <div class="hm-detail-kpi"><div class="hm-detail-kpi-val">${peakStr}</div><div class="hm-detail-kpi-lbl">Peak Hour</div></div>
    </div>
    <div class="hm-detail-section-title">Breakdown by Rule</div>
    <div class="hm-detail-rules">${ruleRows}</div>
    ${vehicleSection}
    ${driverSection}
    ${hourChart}
    <div class="hm-detail-section-title">Coordinates</div>
    <div class="hm-detail-coords">${zone.centroidLat.toFixed(5)}, ${zone.centroidLng.toFixed(5)}</div>
    <div class="hm-detail-view-row">
      <a class="tb-btn hm-gmaps-btn" href="https://www.google.com/maps?q=${zone.centroidLat},${zone.centroidLng}" target="_blank" rel="noopener">↗ Google Maps</a>
      <a class="tb-btn hm-gmaps-btn" href="https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${zone.centroidLat},${zone.centroidLng}" target="_blank" rel="noopener">↗ Street View</a>
    </div>`;

  el('heatmapDetailPanel')?.classList.add('active');
  if (_map) _map.flyTo({ center: [zone.centroidLng, zone.centroidLat], zoom: 15, duration: 800 });
}

// ── KPI bar ─────────────────────────────────────────────────────────────────

function renderHeatmapKpis(points: HeatPoint[], zones: DangerZone[]): void {
  const ruleCounts: Partial<Record<RuleKey, number>> = {};
  for (const p of points) ruleCounts[p.ruleKey] = (ruleCounts[p.ruleKey] ?? 0) + 1;
  const topRuleEntry = (Object.entries(ruleCounts) as [RuleKey, number][])
    .sort((a, b) => b[1] - a[1])[0];

  setText('hmKpiEvents',   points.length.toLocaleString());
  setText('hmKpiVehicles', new Set(points.map(p => p.deviceId)).size.toString());
  setText('hmKpiDrivers',  new Set(points.filter(p => p.driverId).map(p => p.driverId!)).size.toString());
  setText('hmKpiTopRule',  topRuleEntry ? RULE_VISUALS[topRuleEntry[0]].label : '—');
  setText('hmKpiZones',    zones.length.toString());

  const pill = document.querySelector('.hm-kpi-pill-rule span:first-child') as HTMLElement | null;
  if (pill && topRuleEntry) pill.style.color = RULE_VISUALS[topRuleEntry[0]].color;
  else if (pill) pill.style.color = '';
}

// ── Top Offenders panel ──────────────────────────────────────────────────────

function renderTopOffenders(points: HeatPoint[]): void {
  const list = el('hmOffendersList');
  if (!list || !_pageData) return;
  const nameMap = new Map(_pageData.deviceList.map(d => [d.id, d.name]));

  const byVehicle = new Map<string, { count: number; rules: Partial<Record<RuleKey, number>> }>();
  for (const p of points) {
    if (!byVehicle.has(p.deviceId)) byVehicle.set(p.deviceId, { count: 0, rules: {} });
    const v = byVehicle.get(p.deviceId)!;
    v.count++;
    v.rules[p.ruleKey] = (v.rules[p.ruleKey] ?? 0) + 1;
  }
  const sorted = [...byVehicle.entries()].sort((a, b) => b[1].count - a[1].count).slice(0, 10);

  if (!sorted.length) {
    list.innerHTML = '<div style="color:var(--muted);font-size:0.7rem;padding:0.5rem 0">No data</div>';
    return;
  }
  list.innerHTML = sorted.map(([devId, data], i) => {
    const name    = nameMap.get(devId) ?? devId;
    const topRule = (Object.entries(data.rules) as [RuleKey, number][]).sort((a, b) => b[1] - a[1])[0];
    const color   = topRule ? RULE_VISUALS[topRule[0]].color : '#888';
    const label   = topRule ? RULE_VISUALS[topRule[0]].label : '';
    return `<div class="hm-zone-row">
      <div class="hm-zone-rank">${i + 1}</div>
      <div class="hm-rule-dot" style="background:${color}"></div>
      <div class="hm-zone-info">
        <div class="hm-zone-name" title="${escapeHtmlAttr(name)}">${escapeHtml(name)}</div>
        <div class="hm-zone-meta">${label} · ${data.count} violations</div>
      </div>
      <div class="hm-zone-count" style="color:${color}">${data.count}</div>
    </div>`;
  }).join('');
}

// ── Violation Events table ───────────────────────────────────────────────────

function renderEventsTable(points: HeatPoint[]): void {
  const tbl = el('hmEventsTable');
  const cnt = el('hmEventsCount');
  if (!tbl || !_pageData) return;

  const nameMap   = new Map(_pageData.deviceList.map(d => [d.id, d.name]));
  const pointZone = new WeakMap<HeatPoint, DangerZone>();
  for (const z of _dangerZones) for (const p of z.points) pointZone.set(p, z);

  const sorted  = [...points].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const display = sorted.slice(0, 500);

  if (!points.length) {
    if (cnt) cnt.textContent = '0 events';
    tbl.innerHTML = `<tbody><tr><td colspan="6" style="text-align:center;color:var(--muted);padding:2rem 1rem;">No violation events match the current filters.</td></tr></tbody>`;
    return;
  }

  if (cnt) cnt.textContent = points.length > 500
    ? `Showing 500 of ${points.length.toLocaleString()} events`
    : `${points.length.toLocaleString()} events`;

  const thead = `<thead><tr>
    <th>#</th><th>Vehicle</th><th>Driver</th><th>Rule</th><th>Date / Time</th><th>Location</th>
  </tr></thead>`;

  const tbody = display.map((p, i) => {
    const vehicle = nameMap.get(p.deviceId) ?? p.deviceId;
    const driver  = p.driverId ? (_pageData!.userMap.get(p.driverId) ?? '—') : '—';
    const color   = RULE_VISUALS[p.ruleKey].color;
    const label   = RULE_VISUALS[p.ruleKey].label;
    const dt      = new Date(p.timestamp).toLocaleString();
    const zone    = pointZone.get(p);
    const loc     = zone?.locationName ?? `${p.latitude.toFixed(4)}, ${p.longitude.toFixed(4)}`;
    return `<tr>
      <td class="td-mono td-muted">${i + 1}</td>
      <td>${escapeHtml(vehicle)}</td>
      <td class="td-muted">${escapeHtml(driver)}</td>
      <td><span class="hm-rule-dot" style="background:${color};vertical-align:middle;margin-right:4px;display:inline-block"></span>${label}</td>
      <td class="td-mono td-muted" style="white-space:nowrap">${dt}</td>
      <td class="td-muted" style="font-size:0.68rem">${escapeHtml(loc)}</td>
    </tr>`;
  }).join('');

  tbl.innerHTML = thead + `<tbody>${tbody}</tbody>`;
}

// ── Render helpers ──────────────────────────────────────────────────────────

function renderZoneTable(zones: DangerZone[]): void {
  const list = el('heatmapZoneList');
  if (!list) return;
  if (zones.length === 0) {
    list.innerHTML = '<div style="color:var(--muted);font-size:0.7rem;padding:0.5rem 0">No clusters found. Try a wider date range or lower radius.</div>';
    return;
  }
  list.innerHTML = zones.map(z => {
    const color = RULE_VISUALS[z.dominantRule].color;
    const name  = z.locationName ?? `${z.centroidLat.toFixed(3)}, ${z.centroidLng.toFixed(3)}`;
    return `
      <div class="hm-zone-row" data-zone-id="${z.id}">
        <div class="hm-zone-rank">${z.rank}</div>
        <div class="hm-rule-dot" style="background:${color}"></div>
        <div class="hm-zone-info">
          <div class="hm-zone-name" title="${escapeHtmlAttr(name)}">${escapeHtml(name)}</div>
          <div class="hm-zone-meta">${RULE_VISUALS[z.dominantRule].label} · ${z.uniqueDeviceIds.size} vehicles</div>
        </div>
        <div class="hm-zone-count" style="color:${color}">${z.totalViolations}</div>
      </div>`;
  }).join('');

  list.querySelectorAll('.hm-zone-row').forEach(row => {
    row.addEventListener('click', () => {
      const zone = zones.find(z => z.id === (row as HTMLElement).dataset.zoneId);
      if (zone) onZoneClick(zone);
    });
  });
}

// ── Background geocoding (parallel — Geotab API handles concurrent calls) ──

function geocodeZonesBackground(zones: DangerZone[]): void {
  const snapshot = [...zones];
  (async () => {
    await Promise.all(snapshot.map(async zone => {
      if (zone.locationName) return;
      zone.locationName = await reverseGeocode(zone.centroidLat, zone.centroidLng);
    }));
    if (snapshot.some(z => _dangerZones.some(dz => dz.id === z.id))) {
      renderZoneTable(_dangerZones);
      // Update open detail panel title if it still shows a raw Zone # label
      const panel = el('heatmapDetailPanel');
      const title = el('heatmapDetailTitle');
      if (panel?.classList.contains('active') && title) {
        const zone = snapshot.find(z => title.textContent === `Zone #${z.rank}` && z.locationName);
        if (zone) title.textContent = zone.locationName!;
      }
    }
  })();
}

// ── Export ──────────────────────────────────────────────────────────────────

function wireExportButton(): void {
  el('heatmapExportBtn')?.addEventListener('click', async () => {
    if (_dangerZones.length === 0) return;
    const btn = el<HTMLButtonElement>('heatmapExportBtn')!;
    const orig = btn.textContent ?? '↓ Export XLSX';
    btn.textContent = 'Preparing…'; btn.disabled = true;
    try {
      const fromISO = el<HTMLInputElement>('heatmapFrom')?.value ?? '';
      const toISO   = el<HTMLInputElement>('heatmapTo')?.value   ?? '';
      await exportHeatmapXlsx(_dangerZones, _allPoints, fromISO, toISO);
    } finally {
      btn.textContent = orig; btn.disabled = false;
    }
  });
}

// ── State display ───────────────────────────────────────────────────────────

type ViewState = 'empty' | 'loading' | 'results' | 'error';

function showState(s: ViewState, msg?: string): void {
  const ids: Record<string, boolean> = {
    heatmapEmpty:   s === 'empty',
    heatmapLoading: s === 'loading',
    heatmapResults: s === 'results',
    heatmapErrBox:  s === 'error',
  };
  for (const [id, show] of Object.entries(ids)) el(id)?.classList.toggle('hidden', !show);
  if (s === 'error' && msg) { const box = el('heatmapErrBox'); if (box) box.textContent = msg; }
  if (s !== 'results') showExportButton(false);
}

function showError(msg: string): void { showState('error', msg); }

function showExportButton(show: boolean): void {
  const btn = el<HTMLButtonElement>('heatmapExportBtn');
  if (btn) btn.style.display = show ? '' : 'none';
}

// ── Vehicle selector ───────────────────────────────────────────────────────

function buildHmVehicleSelector(): void {
  if (!_pageData) return;
  const list = el('hmVsList');
  if (!list) return;
  list.innerHTML = '';

  const groupMap = new Map<string, string>();
  state.allVehicles.forEach(v => groupMap.set(v.device.id, v.group ?? 'No Group'));

  const byGroup: Record<string, Array<{ id: string; name: string }>> = {};
  for (const d of _pageData.deviceList) {
    const g = groupMap.get(d.id) ?? 'No Group';
    if (!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(d);
  }

  const groupNames = Object.keys(byGroup).sort((a, b) => {
    if (a === 'No Group') return 1;
    if (b === 'No Group') return -1;
    return a.localeCompare(b);
  });

  for (const gName of groupNames) {
    const hdr = document.createElement('div');
    hdr.className   = 'vselector-group-hdr';
    hdr.textContent = gName + ' (' + byGroup[gName].length + ')';
    list.appendChild(hdr);

    for (const d of byGroup[gName]) {
      const row = document.createElement('label');
      row.className = 'vselector-item';
      const cb = document.createElement('input');
      cb.type    = 'checkbox';
      cb.checked = true;
      (cb as HTMLInputElement & { dataset: DOMStringMap }).dataset.devId = d.id;
      cb.addEventListener('change', updateHmVsLabel);
      const nm = document.createElement('span');
      nm.className   = 'vselector-item-name';
      nm.textContent = d.name;
      row.appendChild(cb);
      row.appendChild(nm);
      list.appendChild(row);
    }
  }
  updateHmVsLabel();

  if (!_hmVsWired) {
    _hmVsWired = true;
    const toggle = el('hmVsToggle');
    const panel  = el('hmVsPanel');
    if (toggle && panel) {
      const resetSearch = () => {
        const inp = el<HTMLInputElement>('hmVsSearch');
        if (inp) inp.value = '';
        document.querySelectorAll<HTMLElement>('#hmVsList .vselector-item, #hmVsList .vselector-group-hdr')
          .forEach(e => e.style.removeProperty('display'));
      };
      toggle.addEventListener('click', e => {
        e.stopPropagation();
        panel.classList.toggle('hidden');
        if (panel.classList.contains('hidden')) resetSearch();
      });
      document.addEventListener('click', () => { panel.classList.add('hidden'); resetSearch(); });
      panel.addEventListener('click', e => e.stopPropagation());
    }
    el('hmVsAll')?.addEventListener('click',  () => {
      document.querySelectorAll<HTMLInputElement>('#hmVsList input').forEach(c => { c.checked = true;  });
      updateHmVsLabel();
    });
    el('hmVsNone')?.addEventListener('click', () => {
      document.querySelectorAll<HTMLInputElement>('#hmVsList input').forEach(c => { c.checked = false; });
      updateHmVsLabel();
    });
    el<HTMLInputElement>('hmVsSearch')?.addEventListener('input', e => {
      const q = (e.target as HTMLInputElement).value.toLowerCase().trim();
      document.querySelectorAll<HTMLElement>('#hmVsList .vselector-item').forEach(row => {
        const name = row.querySelector('.vselector-item-name')?.textContent?.toLowerCase() ?? '';
        row.style.display = !q || name.includes(q) ? '' : 'none';
      });
      document.querySelectorAll<HTMLElement>('#hmVsList .vselector-group-hdr').forEach(hdr => {
        if (!q) { hdr.style.removeProperty('display'); return; }
        let next = hdr.nextElementSibling as HTMLElement | null;
        let hasVisible = false;
        while (next && !next.classList.contains('vselector-group-hdr')) {
          if (next.style.display !== 'none') { hasVisible = true; break; }
          next = next.nextElementSibling as HTMLElement | null;
        }
        hdr.style.display = hasVisible ? '' : 'none';
      });
    });
  }
}

function updateHmVsLabel(): void {
  const all     = document.querySelectorAll<HTMLInputElement>('#hmVsList input[type="checkbox"]');
  const checked = Array.from(all).filter(c => c.checked);
  const toggle  = el('hmVsToggle');
  if (toggle) {
    toggle.textContent = checked.length === all.length
      ? 'All ' + all.length + ' vehicles ▾'
      : checked.length + ' of ' + all.length + ' vehicles ▾';
  }
  _selectedHmDeviceIds = checked.length === all.length
    ? null
    : new Set(checked.map(c => (c as HTMLInputElement & { dataset: DOMStringMap }).dataset.devId!));
}

// ── Utilities ─────────────────────────────────────────────────────────────

function el<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function setText(id: string, text: string): void {
  const e = el(id); if (e) e.textContent = text;
}

function setLoadingLabel(msg: string): void { setText('heatmapLoadingLabel', msg); }
